import React, { useEffect, useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { LayoutDashboard, CalendarDays, Calendar, Briefcase, Users, Crown, Inbox, Bell, BellRing, Landmark, LogOut, Receipt, Banknote, Building2, Target, Globe, Shield, Settings as SettingsIcon, BarChart3, UserCog, AlertTriangle, Undo2, Trash2, Sparkles, BadgeCheck, FileText, Activity, PanelLeftClose, PanelLeftOpen, ChevronDown, Sun, X, Mail, RefreshCw, TrendingUp, ScanLine, HardDrive, RotateCcw, Move, CheckCircle2, Archive } from "lucide-react";
import { useSidebarDragMode } from "@/lib/useSidebarDragMode";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import BottomNav from "@/components/BottomNav";
import GlobalSearch from "@/components/GlobalSearch";
import InstallPwaButton from "@/components/InstallPwaButton";
import EnablePushButton from "@/components/EnablePushButton";
import ChatBotWidget from "@/components/ChatBotWidget";

const NavItem = ({ to, icon: Icon, label, testid, badge, collapsed }) => {
  const location = useLocation();
  const active = location.pathname === to || (to !== "/dashboard" && location.pathname.startsWith(to));
  return (
    <Link
      to={to}
      data-testid={testid}
      title={collapsed ? label : undefined}
      className={`group flex items-center ${collapsed ? "justify-center px-0" : "gap-3 px-3"} py-2 rounded-md text-sm transition-colors relative ${
        active
          ? "bg-brand-navy text-white"
          : "text-brand-ink/80 hover:bg-brand-orange-100 hover:text-brand-orange-600"
      }`}
    >
      <Icon size={16} className="shrink-0" strokeWidth={1.75} />
      {!collapsed && <span className="font-medium flex-1">{label}</span>}
      {badge ? (
        <span data-testid={`${testid}-badge`} className={`shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full ${
          collapsed ? "absolute -top-1 -right-1" : ""
        } ${
          active ? "bg-white text-brand-navy" : "bg-brand-orange text-white"
        }`}>
          {badge > 99 ? "99+" : badge}
        </span>
      ) : null}
    </Link>
  );
};

/**
 * Iter-37: Collapsible sidebar group. In expanded sidebar mode, the group label is clickable
 * and toggles its body. State is persisted per slug. In icon-only sidebar mode, the body is
 * always rendered (no header) — there's no room for an accordion.
 */
const SidebarGroup = ({ slug, label, collapsed, defaultOpen = true, badge, children }) => {
  const storageKey = `baim_sidebar_group_${slug}`;
  const [open, setOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null) return defaultOpen;
      return raw === "1";
    } catch { return defaultOpen; }
  });
  const toggle = () => {
    setOpen((prev) => {
      const v = !prev;
      try { localStorage.setItem(storageKey, v ? "1" : "0"); } catch {}
      return v;
    });
  };
  if (collapsed) {
    return (
      <>
        <div className="h-3 border-t border-zinc-200/60 mx-2 mt-2" data-testid={`sidebar-group-divider-${slug}`} />
        {children}
      </>
    );
  }
  // Iter-196i — optional group-level "to action" badge. Shown only when
  // the group is collapsed (otherwise the per-row badges already do the job
  // and re-displaying the sum would be visual noise).
  const badgeNum = typeof badge === "number" ? badge : 0;
  const showBadge = !!badgeNum && !open;
  return (
    <div className="flex flex-col" data-testid={`sidebar-group-${slug}`}>
      <button
        type="button"
        onClick={toggle}
        data-testid={`sidebar-group-toggle-${slug}`}
        aria-expanded={open}
        className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-brand-navy/80 font-bold hover:text-brand-navy hover:bg-brand-orange-100/40 px-3 pt-4 pb-1.5 transition-colors rounded-sm"
      >
        <span className="inline-flex items-center gap-1.5">
          <span>{label}</span>
          {showBadge && (
            <span
              data-testid={`sidebar-group-badge-${slug}`}
              className="inline-flex items-center min-w-[18px] h-[16px] px-1 rounded-full bg-amber-500 text-white text-[10px] tracking-normal font-mono font-bold"
              title={`${badgeNum} action${badgeNum > 1 ? "s" : ""} à traiter`}
            >
              {badgeNum > 99 ? "99+" : badgeNum}
            </span>
          )}
        </span>
        <ChevronDown size={12} className={`transition-transform ${open ? "" : "-rotate-90"}`} strokeWidth={2} />
      </button>
      {open && <div className="flex flex-col gap-1">{children}</div>}
    </div>
  );
};

/**
 * Iter-148: visual sub-section header inside a SidebarGroup (e.g. Configuration ▸ Policies / Templates / System).
 * Renders nothing in icon-only mode (no room) so children appear flat.
 *
 * Iter-196k — Now wraps its children inside a sub-section with a subtle left
 * rail and indentation so the nesting between the parent group and its
 * sub-sections is visually obvious. Falling back to the legacy "label-only"
 * markup when `children` is omitted (still used by code paths that haven't
 * migrated yet).
 *
 * Iter-197 — Sub-sections are now ALSO collapsible accordions (per user
 * request). State is persisted per `slug` in localStorage. A small chevron
 * indicates the open/closed state. In icon-only sidebar mode the header
 * disappears and children render flat (no room for an accordion).
 */
const SidebarSubLabel = ({ label, collapsed, testid, slug, defaultOpen = true, children }) => {
  // Derive a stable storage key — prefer the explicit `slug` prop, else fall
  // back to the testid (without the desktop/mobile prefix) so desktop and
  // mobile drawer share the same collapsed state.
  const storageKey = slug
    ? `baim_sidebar_sub_${slug}`
    : `baim_sidebar_sub_${(testid || label || "").replace(/^m?nav-/, "")}`;
  const [open, setOpen] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === null) return defaultOpen;
      return raw === "1";
    } catch { return defaultOpen; }
  });
  const toggle = () => {
    setOpen((prev) => {
      const v = !prev;
      try { localStorage.setItem(storageKey, v ? "1" : "0"); } catch {}
      return v;
    });
  };

  if (collapsed) {
    // In icon-only mode the label is hidden — but we still render the
    // children so the icons appear inline (no nesting cue possible).
    return children || null;
  }
  if (children !== undefined) {
    return (
      <div className="mt-2 ml-3 border-l border-brand-navy/15 pl-2" data-testid={testid}>
        <button
          type="button"
          onClick={toggle}
          data-testid={`${testid}-toggle`}
          aria-expanded={open}
          className="w-full flex items-center justify-between text-[9px] uppercase tracking-[0.18em] text-brand-navy/50 italic font-semibold px-1 pt-1 pb-1 hover:text-brand-navy/80 hover:bg-brand-orange-100/30 rounded-sm transition-colors"
        >
          <span>{label}</span>
          <ChevronDown
            size={10}
            className={`transition-transform ${open ? "" : "-rotate-90"} opacity-60`}
            strokeWidth={2}
          />
        </button>
        {open && <div className="flex flex-col gap-1">{children}</div>}
      </div>
    );
  }
  // Legacy "label-only" rendering — left in place for backwards compat.
  return (
    <div data-testid={testid} className="text-[9px] uppercase tracking-[0.18em] text-brand-navy/45 px-3 pt-3 pb-0.5 font-mono">
      {label}
    </div>
  );
};

export default function AppLayout() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [badges, setBadges] = useState({});
  // Iter-207r — sidebar drag-and-drop edit mode (double-click logo to toggle).
  const sbDrag = useSidebarDragMode();
  // Iter-129: proactive "Re-link Google" badge — checks /auth/me/scopes and lights up the
  // RefreshCw button when at least one bundle (drive/gmail/directory) is missing, so users
  // with legacy tokens (issued before a scope was added) re-link before hitting the error.
  const [scopesMissing, setScopesMissing] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("baim_sidebar_collapsed") === "1"; } catch { return false; }
  });
  // Iter-64: mobile "More" drawer (lists every menu item that can't fit in the bottom-nav).
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const v = !prev;
      try { localStorage.setItem("baim_sidebar_collapsed", v ? "1" : "0"); } catch {}
      return v;
    });
  };

  const loadNotifs = async () => {
    try {
      // Iter-194l — only show UNREAD in the bell dropdown. Once the user
      // clicks one or clicks "Mark all read", it vanishes from the list.
      // The full notification history is still available in the Profile/
      // Settings → Notifications page.
      const r = await api.get("/notifications?unread_only=1");
      setNotifs(r.data);
    } catch {}
    try {
      const b = await api.get("/me/pending-actions");
      setBadges(b.data?.counts || {});
    } catch {}
  };
  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  // Iter-129: poll user scopes on mount + every 5 min so the Re-link badge stays accurate
  // even after the user grants a new bundle in another tab.
  useEffect(() => {
    let cancelled = false;
    const checkScopes = async () => {
      try {
        const r = await api.get("/auth/me/scopes");
        if (cancelled) return;
        const missing = !r.data?.drive || !r.data?.gmail || !r.data?.directory;
        setScopesMissing(missing);
      } catch {
        if (!cancelled) setScopesMissing(false);
      }
    };
    checkScopes();
    const t = setInterval(checkScopes, 300000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Iter-193h: sidebar badge for the admin validation queue (SUBMITTED count).
  const [expenseValidationCount, setExpenseValidationCount] = useState(0);

  useEffect(() => {
    if (!user || !["CEO", "ADMIN", "GESTIONNAIRE"].includes(user.role)) return;
    let cancelled = false;
    const ping = async () => {
      try {
        const r = await api.get("/expenses/admin/queue-counts");
        if (!cancelled) setExpenseValidationCount(r.data?.counts?.SUBMITTED || 0);
      } catch { /* ignore */ }
    };
    ping();
    const t = setInterval(ping, 120000);
    return () => { cancelled = true; clearInterval(t); };
  }, [user]);

  // Iter-171: SW posts {type:"baim-push-navigate", link} when a push notification is clicked
  // and an existing tab is focused — handle it client-side so React Router doesn't reload.
  useEffect(() => {
    const onMsg = (e) => {
      if (e?.data?.type === "baim-push-navigate" && e.data.link) {
        navigate(e.data.link);
      }
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", onMsg);
    }
    return () => {
      if ("serviceWorker" in navigator) navigator.serviceWorker.removeEventListener("message", onMsg);
    };
  }, [navigate]);

  const unread = notifs.filter((n) => !n.read).length;

  if (!user) return null;
  const role = user.role;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    loadNotifs();
  };

  // Iter-196h — sidebar groups for desktop & mobile drawer.
  // Hierarchy: Daily → Operations → Finance → People → Configuration.
  // Iter-207o (2026-06-02) — Finance was re-themed by functional cluster :
  //   Facturation clients · Achats & fournisseurs · Notes de frais ·
  //   Dépenses cabinet · Freelances (finance) · Pilotage.
  // Rationale : each "entry point" (e.g. Bons de commande) now sits next to
  //   its profile (Fournisseurs) and its Drive Health Check, instead of
  //   spreading across Daily / Cash flow / Charges / Config. Daily was kept
  //   minimal — only what every user does each day (Dashboard / Timesheet /
  //   Leaves / Freelance personal pages). Freelance settings + Freelance
  //   flow templates moved out of Configuration into Finance ▸ Freelances.
  const renderNavGroups = ({ prefix, collapsed: c, slugSuffix }) => {
    const tid = (key) => `${prefix}-${key}`;
    const sg = (slug) => `${slug}${slugSuffix}`;
    // Iter-228 — sidebar gating is now permission-based (hasPermission) wherever
    // a permission key exists in PERMISSION_CATALOG. Pure-CEO super-admin
    // actions keep the role check as fallback.
    const has = (p) => hasPermission(p);
    const canViewMissions = has("manage_missions") || has("view_all_missions") || has("manage_own_clients");
    const canValidateCra = has("validate_cra");
    const canExportCra = has("export_cra");
    const canInvoicing = has("invoicing");
    const canValidateExpenses = has("validate_expenses") || has("view_all_expenses");
    const canManageCabinet = has("manage_cabinet_expenses");
    const canSubmitExpenses = has("submit_expenses");
    const canOutgoingPO = has("manage_outgoing_pos");
    const canViewFreelanceInvoices = has("view_all_freelance_invoices") || has("manage_freelance_invoices");
    const canManageUsers = has("manage_users");
    const canManageDomains = has("manage_domains");
    const canManageSettings = has("manage_settings");
    const canManageCustomRoles = has("manage_custom_roles");
    const canManageRolePerms = has("manage_role_permissions");
    const canAccessDirectory = has("access_directory");
    const canManageDrive = has("manage_drive");
    const canManageInvoiceReminders = has("manage_invoice_reminders");
    const canValidatePayments = has("validate_payment_declarations");
    const canViewProfitability = has("view_profitability");
    const canViewArAging = has("view_ar_aging");
    const canViewGlobalOverview = has("view_global_overview");
    const canRunSimulations = has("run_simulations");
    const canAutoInvoice = has("auto_invoice");
    // Submitting your own freelance invoice — only relevant if the user IS
    // a freelance (keeps the personal "Mes factures" item visible).
    const canSubmitFreelanceInvoice = has("submit_freelance_invoice");
    // Spending section visibility: shown if the user has ANY spending-related
    // entry to surface (NDF / Cabinet / Achats).
    const hasSpendingSection = canSubmitExpenses || canValidateExpenses || canManageCabinet || canOutgoingPO;
    return (
      <>
        {/* 1. Daily — what every user does each day (minimal). */}
        <SidebarGroup slug={sg("daily")} label="Daily" collapsed={c}>
          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid={tid("dashboard")} collapsed={c} />
          {has("timesheet") && (
            <NavItem to="/timesheet" icon={CalendarDays} label="Timesheet" testid={tid("timesheet")} collapsed={c} />
          )}
          {!user.is_freelance && (
            <NavItem to="/conges" icon={Sun} label="Leaves" testid={tid("conges")} collapsed={c} />
          )}
          {user.is_freelance && (
            <>
              <NavItem to="/freelance/me" icon={BadgeCheck} label="Infos pro" testid={tid("freelance-me")} collapsed={c} />
              {canSubmitFreelanceInvoice && (
                <NavItem to="/freelance/me/invoices" icon={FileText} label="Mes factures" testid={tid("freelance-my-invoices")} badge={badges.my_invoices_to_submit} collapsed={c} />
              )}
            </>
          )}
        </SidebarGroup>

        {/* 2. Spending — NDF + Cabinet + Achats. */}
        {hasSpendingSection && (
          <SidebarGroup slug={sg("spending")} label="Spending" collapsed={c} defaultOpen={false}>
            {/* Iter-228x-bis — Global expense search shortcut at the top of
                the Spending group. Always shown when the section is visible. */}
            <NavItem to="/expenses/search" icon={ScanLine} label="Recherche globale" testid={tid("expenses-search")} collapsed={c} />
            {(canSubmitExpenses || canValidateExpenses) && (
              <SidebarSubLabel label="Notes de frais" collapsed={c} testid={tid("spending-sub-ndf")}>
                {canSubmitExpenses && (
                  <NavItem to="/expenses" icon={ScanLine} label="Saisie / Mes NDF" testid={tid("expenses")} collapsed={c} />
                )}
                {canValidateExpenses && (
                  <NavItem to="/admin/expenses/validation" icon={ScanLine} label="NDF à valider" testid={tid("expenses-validation")} badge={expenseValidationCount || undefined} collapsed={c} />
                )}
                {canManageDrive && (
                  <NavItem to="/admin/ndf-drive-health" icon={HardDrive} label="NDF Drive health" testid={tid("ndf-drive-health")} collapsed={c} />
                )}
              </SidebarSubLabel>
            )}
            {canManageCabinet && (
              <SidebarSubLabel label="Dépenses cabinet" collapsed={c} testid={tid("spending-sub-cabinet")}>
                <NavItem to="/cabinet-expenses" icon={ScanLine} label="Saisie des dépenses" testid={tid("cabinet-expenses")} collapsed={c} />
                {canManageDrive && (
                  <NavItem to="/admin/cabinet-expenses-drive-health" icon={HardDrive} label="Cabinet Drive health" testid={tid("cabinet-expenses-drive-health")} collapsed={c} />
                )}
              </SidebarSubLabel>
            )}
            {canOutgoingPO && (
              <SidebarSubLabel label="Achats & fournisseurs" collapsed={c} testid={tid("spending-sub-procurement")}>
                <NavItem to="/outgoing-pos" icon={FileText} label="Bons de commande" testid={tid("outgoing-pos")} collapsed={c} />
                <NavItem to="/suppliers" icon={Building2} label="Fournisseurs" testid={tid("suppliers")} collapsed={c} />
                {canManageDrive && (
                  <NavItem to="/admin/suppliers-drive-health" icon={HardDrive} label="Fournisseurs Drive health" testid={tid("suppliers-drive-health")} collapsed={c} />
                )}
                {canManageSettings && (
                  <NavItem to="/settings/maintenance/outgoing-po-email" icon={Mail} label="PO email template" testid={tid("maintenance-outgoing-po-email")} collapsed={c} />
                )}
                {canManageSettings && (
                  <NavItem to="/settings/maintenance/monthly-statement-email" icon={Mail} label="État mensuel — email" testid={tid("maintenance-monthly-statement-email")} collapsed={c} />
                )}
              </SidebarSubLabel>
            )}
          </SidebarGroup>
        )}

        {/* 3. Operations — delivery, validation, who's-doing-what */}
        {(canViewMissions || canValidateCra || canExportCra) && (
          <SidebarGroup slug={sg("operations")} label="Operations" collapsed={c}>
            {canRunSimulations && (
              <NavItem to="/simulations" icon={Sparkles} label="Simulations" testid={tid("simulations")} collapsed={c} />
            )}
            {canViewMissions && (
              <NavItem to="/missions" icon={Briefcase} label="Missions" testid={tid("missions")} badge={badges.missions_action} collapsed={c} />
            )}
            {canViewMissions && (
              <NavItem to="/prospects" icon={Target} label="Prospects" testid={tid("prospects")} collapsed={c} />
            )}
            {canViewMissions && (
              <NavItem to="/clients" icon={Building2} label="Clients" testid={tid("clients")} collapsed={c} />
            )}
            {canValidateCra && (
              <NavItem to="/manager/requests" icon={Inbox} label="CRA Validation" testid={tid("requests")} badge={badges.cra_to_validate} collapsed={c} />
            )}
            {canValidateCra && (
              <NavItem to="/admin/cra-reminder-manual" icon={BellRing} label="Manual CRA Reminder" testid={tid("cra-reminder-manual")} collapsed={c} />
            )}
            {canExportCra && (
              <NavItem to="/manager/cra-export" icon={FileText} label="CRA Export" testid={tid("cra-export")} badge={badges.cra_exports_pending} collapsed={c} />
            )}
          </SidebarGroup>
        )}

        {/* 4. Finance — facturation, freelances, pilotage */}
        {(canInvoicing || canViewFreelanceInvoices || canViewArAging || canViewGlobalOverview) && (() => {
          const financeBadge = (
            (badges.client_invoices_to_generate || 0) +
            (badges.client_invoices_unsent || 0) +
            (badges.freelance_invoices_total_action || 0) +
            (badges.payment_declarations_pending || 0)
          );
          return (
          <SidebarGroup slug={sg("finance")} label="Finance" collapsed={c} badge={financeBadge}>
            {canInvoicing && (
              <SidebarSubLabel label="Facturation clients" collapsed={c} testid={tid("finance-sub-billing")}>
                <NavItem to="/billing" icon={Banknote} label="Billing" testid={tid("billing")} badge={badges.client_invoices_to_generate} collapsed={c} />
                {canAutoInvoice && (
                  <NavItem to="/admin/auto-invoice" icon={Receipt} label="Auto-invoice" testid={tid("admin-auto-invoice")} collapsed={c} />
                )}
                <NavItem to="/admin/invoicing-audit" icon={AlertTriangle} label="Invoicing audit" testid={tid("admin-invoicing-audit")} collapsed={c} />
                <NavItem to="/invoices" icon={Receipt} label="Client Invoices" testid={tid("invoices")} badge={badges.client_invoices_unsent} collapsed={c} />
                <NavItem to="/invoices/paid" icon={CheckCircle2} label="Factures payées" testid={tid("paid-invoices")} collapsed={c} />
                {canValidatePayments && (
                  <NavItem to="/admin/payment-declarations" icon={Banknote} label="Payment declarations" testid={tid("admin-payment-declarations")} badge={badges.payment_declarations_pending} collapsed={c} />
                )}
                {canManageInvoiceReminders && (
                  <NavItem to="/admin/invoice-reminders-hub" icon={Mail} label="Invoice reminders" testid={tid("admin-invoice-reminders-hub")} collapsed={c} />
                )}
                {/* Iter-228w-bis — Bank accounts moved here (Finance > Facturation Clients)
                    right below Invoice reminders, with Landmark icon. */}
                {canManageSettings && (
                  <NavItem to="/settings/bank-accounts" icon={Landmark} label="Bank accounts" testid={tid("settings-bank-accounts")} collapsed={c} />
                )}
              </SidebarSubLabel>
            )}

            {canViewFreelanceInvoices && (
              <SidebarSubLabel label="Freelances" collapsed={c} testid={tid("finance-sub-freelance")}>
                <NavItem to="/freelance/admin" icon={FileText} label="Freelance Invoices" testid={tid("freelance-admin")} badge={badges.freelance_invoices_total_action} collapsed={c} />
                <NavItem to="/freelance/admin/payments-due" icon={Banknote} label="Payments Due" testid={tid("freelance-payments-due")} collapsed={c} />
                {canManageSettings && (
                  <NavItem to="/settings/freelance" icon={BadgeCheck} label="Freelance settings" testid={tid("settings-freelance")} collapsed={c} />
                )}
                {canManageSettings && (
                  <NavItem to="/settings/maintenance/freelance-flow" icon={BadgeCheck} label="Freelance flow templates" testid={tid("maintenance-freelance-flow")} collapsed={c} />
                )}
              </SidebarSubLabel>
            )}

            <SidebarSubLabel label="Pilotage" collapsed={c} testid={tid("finance-sub-pilotage")}>
              {canInvoicing && (
                <NavItem to="/admin/monthly-statement" icon={Calendar} label="État mensuel" testid={tid("monthly-statement")} collapsed={c} />
              )}
              {canInvoicing && (user?.role === "CEO" || user?.role === "GESTIONNAIRE") && (
                <NavItem to="/admin/real-margin" icon={TrendingUp} label="Marge réelle" testid={tid("real-margin")} collapsed={c} />
              )}
              {canManageInvoiceReminders && (
                <NavItem to="/admin/email-tracking" icon={Mail} label="Email tracking" testid={tid("email-tracking")} collapsed={c} />
              )}
              {canManageDrive && (
                <NavItem to="/admin/drive-overview" icon={HardDrive} label="Drive — Vue d'ensemble" testid={tid("drive-overview")} collapsed={c} />
              )}
              <NavItem to="/stats" icon={BarChart3} label="Stats" testid={tid("stats")} collapsed={c} />
              {canViewProfitability && (
                <NavItem to="/admin/profitability" icon={TrendingUp} label="Profitability" testid={tid("admin-profitability")} collapsed={c} />
              )}
              {canViewArAging && (
                <NavItem to="/ar-aging" icon={Banknote} label="AR aging" testid={tid("ar-aging")} collapsed={c} />
              )}
              {canViewGlobalOverview && (
                <NavItem to="/ceo/overview" icon={Crown} label="Global Overview" testid={tid("ceo-overview")} collapsed={c} />
              )}
              {/* Iter-228 — CEO Mailbox: exclusive to Sami's email account. */}
              {(user.email || "").toLowerCase() === "sami.khanfir@ba-inmotion.com" && (
                <NavItem to="/admin/ceo-mailbox" icon={Mail} label="CEO Mailbox" testid={tid("ceo-mailbox")} collapsed={c} />
              )}
            </SidebarSubLabel>
          </SidebarGroup>
          );
        })()}

        {/* 5. People — user / role management */}
        {(canManageUsers || canManageCustomRoles || canManageRolePerms || canAccessDirectory) && (
          <SidebarGroup slug={sg("people")} label="People" collapsed={c}>
            {canManageUsers && (
              <NavItem to="/collaborators" icon={Users} label="Collaborators" testid={tid("collaborators")} collapsed={c} />
            )}
            {canManageUsers && (
              <NavItem to="/admin/users" icon={UserCog} label="Users & roles" testid={tid("admin-users")} badge={badges.freelances_missing_contract} collapsed={c} />
            )}
            {canManageUsers && (
              <NavItem to="/admin/freelancers" icon={BadgeCheck} label="Freelancers" testid={tid("admin-freelancers")} collapsed={c} />
            )}
            {canManageUsers && (
              <NavItem to="/admin/hr-archives" icon={Archive} label="HR Archives" testid={tid("admin-hr-archives")} collapsed={c} />
            )}
            {canManageRolePerms && (
              <NavItem to="/admin/role-permissions" icon={Shield} label="Role permissions" testid={tid("admin-role-permissions")} collapsed={c} />
            )}
            {canManageCustomRoles && (
              <NavItem to="/admin/custom-roles" icon={Sparkles} label="Custom roles" testid={tid("admin-custom-roles")} collapsed={c} />
            )}
            {canAccessDirectory && (
              <NavItem to="/admin/directory" icon={Users} label="Workspace directory" testid={tid("admin-directory")} collapsed={c} />
            )}
          </SidebarGroup>
        )}

        {/* 6. Configuration — notifications, system, RH policies, templates */}
        {(canManageSettings || canManageDomains) && (
          <SidebarGroup slug={sg("configuration")} label="Configuration" collapsed={c} defaultOpen={false}>
            {canManageSettings && (
              <SidebarSubLabel label="Notifications & relances" collapsed={c} testid={tid("config-sub-notifs")}>
                <NavItem to="/settings/alerts" icon={AlertTriangle} label="Alerts & report" testid={tid("settings-alerts")} collapsed={c} />
                <NavItem to="/settings/notifications" icon={Bell} label="Notifications" testid={tid("settings-notifications")} collapsed={c} />
              </SidebarSubLabel>
            )}

            {canManageSettings && (
              <SidebarSubLabel label="Politiques RH" collapsed={c} testid={tid("config-sub-policies")}>
                <NavItem to="/settings/leaves" icon={CalendarDays} label="Leaves & holidays" testid={tid("settings-leaves")} collapsed={c} />
              </SidebarSubLabel>
            )}

            {canManageSettings && (
              <SidebarSubLabel label="Templates CRA" collapsed={c} testid={tid("config-sub-templates")}>
                <NavItem to="/settings/maintenance/cra-export" icon={FileText} label="CRA Export template" testid={tid("maintenance-cra-export")} collapsed={c} />
                <NavItem to="/settings/maintenance/weekly-cra" icon={FileText} label="Weekly CRA template" testid={tid("maintenance-weekly-cra")} collapsed={c} />
                <NavItem to="/settings/maintenance/monthly-cra" icon={FileText} label="Monthly CRA template" testid={tid("maintenance-monthly-cra")} collapsed={c} />
              </SidebarSubLabel>
            )}

            {(canManageSettings || canManageDomains) && (
              <SidebarSubLabel label="Système" collapsed={c} testid={tid("config-sub-system")}>
                {canManageSettings && (
                  <NavItem to="/settings/maintenance/general" icon={SettingsIcon} label="General maintenance" testid={tid("maintenance-general")} collapsed={c} />
                )}
                {canManageSettings && (
                  <NavItem to="/admin/cra-reminder-setup" icon={SettingsIcon} label="Reminder Setup" testid={tid("cra-reminder-setup")} collapsed={c} />
                )}
                {canManageSettings && (
                  <NavItem to="/recent-activity" icon={Activity} label="Recent activity" testid={tid("recent-activity")} badge={badges.recent_activity_unseen} collapsed={c} />
                )}
                {canManageDomains && (
                  <NavItem to="/admin/domains" icon={Globe} label="Allowed domains" testid={tid("admin-domains")} collapsed={c} />
                )}
                {canManageSettings && (
                  <NavItem to="/settings/maintenance/logs" icon={FileText} label="Logs" testid={tid("maintenance-logs")} collapsed={c} />
                )}
              </SidebarSubLabel>
            )}
          </SidebarGroup>
        )}
      </>
    );
  };

  return (
    <div className="h-screen overflow-hidden flex bg-[#F8F3E8]">
      {/* Sidebar — desktop / tablet only (hidden on mobile, BottomNav takes over) */}
      {/* Iter-207aa — Single-click on the BAIM logo redirects to flowman.net
          (the parent brand portal). Double-click stays reserved for the
          sidebar drag-and-drop edit mode. To distinguish them, the single-
          click handler delays its action 250ms — if a 2nd click arrives in
          that window it's a dblclick → cancel the redirect. */}
      <aside className={`${collapsed ? "w-16" : "w-60"} shrink-0 border-r border-brand-navy/15 bg-[#FAF6EE] hidden md:flex flex-col h-full transition-[width] duration-200 ${sbDrag.editMode ? "ring-2 ring-blue-400 ring-inset" : ""}`}>
        <div className={`h-16 shrink-0 flex items-center border-b border-brand-navy/15 bg-[#F5EBD6] ${collapsed ? "justify-center px-0" : "px-5 gap-3"}`}>
          <img
            src="/logo.png"
            alt="BAIM"
            className="w-9 h-9 object-contain cursor-pointer select-none"
            data-testid="app-logo-sidebar"
            onClick={(e) => {
              e.preventDefault();
              // Cancel any pending redirect (defensive — should never happen
              // when a real dblclick races us, since we'd already have set
              // the timer below).
              if (window.__baimLogoTimer) {
                clearTimeout(window.__baimLogoTimer);
                window.__baimLogoTimer = null;
              }
              window.__baimLogoTimer = setTimeout(() => {
                window.__baimLogoTimer = null;
                window.location.href = "https://flowman.net";
              }, 260);
            }}
            onDoubleClick={(e) => {
              e.preventDefault();
              // Eat the pending single-click redirect.
              if (window.__baimLogoTimer) {
                clearTimeout(window.__baimLogoTimer);
                window.__baimLogoTimer = null;
              }
              if (!collapsed) sbDrag.onLogoDoubleClick();
            }}
            title={collapsed ? "Aller sur flowman.net" : "Cliquez pour flowman.net · Double-clic pour le mode édition sidebar"}
            draggable={false}
          />
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold text-[13px] tracking-tight leading-none text-brand-navy">BAIM</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-navy-500 mt-1">Mission Control</div>
            </div>
          )}
          {/* Iter-207r — Reset icon only visible in edit mode. */}
          {!collapsed && sbDrag.editMode && (
            <button
              onClick={sbDrag.resetToDefault}
              data-testid="sidebar-reset-layout"
              title="Réinitialiser au layout standard"
              className="p-1.5 rounded text-red-600 hover:bg-red-100 shrink-0"
            >
              <RotateCcw size={14} strokeWidth={2} />
            </button>
          )}
          <button
            onClick={toggleCollapsed}
            data-testid="sidebar-toggle"
            title={collapsed ? "Expand the menu" : "Collapse the menu"}
            className="p-1.5 rounded hover:bg-brand-orange-100 text-brand-navy-500 hover:text-brand-orange-600 shrink-0"
          >
            {collapsed ? <PanelLeftOpen size={16} strokeWidth={1.75} /> : <PanelLeftClose size={16} strokeWidth={1.75} />}
          </button>
        </div>

        {/* Iter-207r — Edit mode banner. */}
        {!collapsed && sbDrag.editMode && (
          <div className="px-3 py-2 bg-blue-50 border-b border-blue-200 text-[11px] text-blue-800 flex items-center gap-1.5" data-testid="sidebar-edit-banner">
            <Move size={11} />
            <span className="leading-tight">
              <strong>Mode édition</strong> — glissez les briques. Double-clic logo pour sauver. Échap pour annuler.
            </span>
          </div>
        )}

        <nav data-testid="sidebar-nav" className={`flex-1 min-h-0 overflow-y-auto flex flex-col gap-1 sidebar-nav-scroll-left ${collapsed ? "p-2" : "p-3"}`}>
          {renderNavGroups({ prefix: "nav", collapsed, slugSuffix: "" })}
        </nav>

        <div className={`shrink-0 border-t border-zinc-200 ${collapsed ? "p-2" : "p-3"}`}>
          <div className={`flex items-center py-2 ${collapsed ? "flex-col gap-2" : "gap-3 px-2"}`}>
            {user.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" data-testid="sidebar-user-avatar" referrerPolicy="no-referrer" title={collapsed ? user.name : undefined} />
            ) : (
              <div className="w-8 h-8 bg-zinc-100 text-zinc-700 rounded-full flex items-center justify-center text-xs font-medium uppercase" title={collapsed ? user.name : undefined}>
                {user.name?.slice(0, 2) || "U"}
              </div>
            )}
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-900 truncate" data-testid="sidebar-user-name">{user.name}</div>
                <div className="text-[11px] text-zinc-500 uppercase tracking-wider font-mono" data-testid="sidebar-user-role">
                  {user.is_freelance && role === "COLLABORATOR" ? "Collaborateur Freelance" : role}
                </div>
              </div>
            )}
            <button
              onClick={() => navigate("/account/connections")}
              data-testid="relink-google-btn"
              title={collapsed ? (scopesMissing ? "Google account · scopes missing" : "Google account permissions") : (scopesMissing ? "Google account — at least one scope is missing, click to manage" : "Manage Google account permissions")}
              className="relative p-2 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 rounded-md"
            >
              <RefreshCw size={15} strokeWidth={1.75} />
              {scopesMissing && (
                <span
                  data-testid="relink-google-badge"
                  className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-orange-500 ring-2 ring-[#FAF6EE]"
                  aria-label="Google scopes missing"
                />
              )}
            </button>
            <button onClick={logout} data-testid="logout-btn" title={collapsed ? "Sign out" : undefined} className="p-2 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 rounded-md">
              <LogOut size={15} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col h-full">
        {user.is_test && <SandboxBanner />}
        {/* Iter-209 — CEO prod-impersonation banner. Yellow, sticky at top
            of the main area. Shown whenever the current session is a
            prod-impersonation (vs sandbox which shows SandboxBanner). */}
        {user.impersonation && !user.is_test && <ProdImpersonationBanner meta={user.impersonation} />}
        {user.status === "SUSPENDED" && (
          <div data-testid="suspended-banner" className="px-4 py-2 bg-red-700 text-white text-xs font-medium tracking-wide flex items-center justify-center gap-2">
            <Shield size={14} /> Account suspended — read-only access. Contact your administrator.
          </div>
        )}
        <header className="h-16 shrink-0 bg-[#FAF6EE]/80 backdrop-blur-md border-b border-brand-navy/15 flex items-center justify-between px-3 md:px-8 gap-2 md:gap-3">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            {/* Mobile branding (no sidebar visible at this breakpoint) */}
            <img
              src="/logo.png"
              alt="BAIM"
              className="md:hidden w-7 h-7 object-contain shrink-0 cursor-pointer select-none"
              data-testid="app-logo-mobile-header"
              onClick={(e) => {
                e.preventDefault();
                // Iter-207aa — mobile header logo single-tap → flowman.net.
                window.location.href = "https://flowman.net";
              }}
              draggable={false}
            />
            <div className="text-[10px] md:text-[11px] uppercase tracking-[0.15em] md:tracking-[0.25em] text-brand-navy-500 font-mono truncate min-w-0">{role}{user.is_test ? " · SBX" : ""}</div>
          </div>
          {/* Iter-87: global search bar — Ctrl+K shortcut, 10 hits, keyboard-navigable. */}
          <div className="hidden md:flex flex-1 justify-center min-w-0">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-1.5 md:gap-3 shrink-0">
            {/* Real prod CEO: single button to enter sandbox (as sandbox CEO) */}
            {role === "CEO" && !user.is_test && <ProdCeoSandboxToggle />}
            {/* Inside sandbox: show switcher between profiles */}
            {user.is_test && <SandboxSwitcher isSandboxUser={true} />}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="notif-btn" data-testid-alt="notif-bell" className="relative p-2 rounded-md hover:bg-zinc-100 transition-colors" aria-label="Notifications">
                  <Bell size={17} strokeWidth={1.75} className="text-zinc-700" />
                  {unread > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-600" data-testid="notif-badge" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-96 max-h-[70vh] overflow-y-auto">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Notifications</span>
                  {unread > 0 && (
                    <button data-testid="notif-markall" onClick={markAll} className="text-xs text-zinc-500 hover:text-zinc-900">
                      Mark all read
                    </button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.length === 0 && (
                  <div className="px-3 py-6 text-sm text-zinc-500 text-center">No notifications</div>
                )}
                {/* Iter-192 — group notifications by day (Today/Yesterday/full date). */}
                {(() => {
                  if (notifs.length === 0) return null;
                  const today = new Date();
                  const yesterday = new Date(today);
                  yesterday.setDate(today.getDate() - 1);
                  const ymd = (d) => d.toISOString().slice(0, 10);
                  const todayKey = ymd(today);
                  const yKey = ymd(yesterday);
                  const fmtDay = (key) => {
                    if (key === todayKey) return "Aujourd'hui";
                    if (key === yKey) return "Hier";
                    const d = new Date(key + "T12:00:00");
                    // Within last 7 days → weekday + day
                    const diffDays = Math.round((today - d) / 86400000);
                    if (diffDays < 7) {
                      return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
                    }
                    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
                  };
                  const fmtTime = (iso) => {
                    if (!iso) return "";
                    try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
                  };
                  // Group while preserving order (notifs are already sorted desc by created_at server-side).
                  const groups = [];
                  for (const n of notifs.slice(0, 30)) {
                    const k = (n.created_at || "").slice(0, 10) || todayKey;
                    let g = groups.length > 0 && groups[groups.length - 1].key === k ? groups[groups.length - 1] : null;
                    if (!g) {
                      g = { key: k, items: [] };
                      groups.push(g);
                    }
                    g.items.push(n);
                  }
                  return groups.map((g) => (
                    <div key={g.key} data-testid={`notif-group-${g.key}`}>
                      <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] font-mono text-zinc-500 bg-zinc-50 border-t border-zinc-100">
                        {fmtDay(g.key)}
                      </div>
                      {g.items.map((n) => (
                        <DropdownMenuItem
                          key={n.notification_id}
                          data-testid={`notif-item-${n.notification_id}`}
                          className={`flex flex-col items-start gap-1 cursor-pointer ${!n.read ? "bg-zinc-50/70" : ""}`}
                          onClick={() => {
                            api.post(`/notifications/${n.notification_id}/read`).then(loadNotifs);
                            if (n.link) navigate(n.link);
                          }}
                        >
                          <div className="w-full flex items-center justify-between gap-2">
                            <span className="text-[10px] text-zinc-500 font-mono uppercase">{(n.type || n.kind || "").toString().replace(/_/g, " ")}</span>
                            <span className="text-[10px] text-zinc-400 font-mono">{fmtTime(n.created_at)}</span>
                          </div>
                          <span className="text-sm text-zinc-900 leading-snug whitespace-pre-line">{n.message || n.title || n.body || ""}</span>
                        </DropdownMenuItem>
                      ))}
                    </div>
                  ));
                })()}
              </DropdownMenuContent>
            </DropdownMenu>
            {user.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover border border-zinc-200" data-testid="header-user-avatar" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-zinc-100 text-zinc-700 flex items-center justify-center text-xs font-medium uppercase border border-zinc-200" data-testid="header-user-avatar">
                {user.name?.slice(0, 2) || "U"}
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 min-h-0 overflow-y-auto pb-20 md:pb-0" data-testid="main-scroll-area">
          <Outlet />
        </main>
      </div>

      {/* Iter-64: mobile bottom navigation (md:hidden inside the component) */}
      <BottomNav user={user} badges={badges} onMore={() => setMobileMoreOpen(true)} />

      {/* Iter-194x — Floating help chatbot. Iter-228x-bis: gated by
          `use_chatbot` so a user whose permission has been revoked no
          longer sees the widget. */}
      {hasPermission("use_chatbot") && <ChatBotWidget />}

      {/* Iter-65: full-screen "More" drawer on mobile, lists all menu items not in the bottom nav.
          Iter-66 fix: the drawer ends ABOVE the bottom-nav (not behind it) so the Sign out
          button at the bottom remains tappable on every device. */}
      {mobileMoreOpen && (
        <div
          className="fixed inset-0 z-[10001] md:hidden"
          data-testid="mobile-more-overlay"
          onClick={() => setMobileMoreOpen(false)}
        >
          {/* Backdrop only covers the area ABOVE the bottom-nav so the nav stays visible */}
          <div
            className="absolute inset-x-0 top-0 bg-black/40"
            style={{ bottom: "calc(60px + env(safe-area-inset-bottom))" }}
          />
          <div
            className="absolute right-0 top-0 w-[82%] max-w-sm bg-[#FAF6EE] flex flex-col shadow-2xl"
            style={{ bottom: "calc(60px + env(safe-area-inset-bottom))" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="mobile-more-drawer"
          >
            <div className="h-16 px-5 flex items-center justify-between border-b border-brand-navy/15 bg-[#F5EBD6]">
              <div className="font-heading font-bold text-sm text-brand-navy">Menu</div>
              <button
                onClick={() => setMobileMoreOpen(false)}
                data-testid="mobile-more-close"
                className="p-2 rounded hover:bg-brand-orange-100 text-brand-navy"
              >
                <X size={18} />
              </button>
            </div>
            <div
              className="flex-1 overflow-y-auto p-3 flex flex-col gap-1"
              onClick={(e) => {
                // Iter-196j — only close the drawer when the user actually
                // navigates (click on a NavItem <Link>). Clicks on the group
                // collapse/expand buttons used to bubble up and close the
                // drawer too, which made deep menus unreachable on mobile.
                if (e.target.closest && e.target.closest("a")) {
                  setMobileMoreOpen(false);
                }
              }}
            >
              {renderNavGroups({ prefix: "mnav", collapsed: false, slugSuffix: "-mobile" })}
            </div>
            <div className="border-t border-zinc-200 px-3 py-3 space-y-2">
              <EnablePushButton testid="mobile-more-enable-push" />
              <InstallPwaButton testid="mobile-more-install-pwa" />
              <button
                onClick={() => {
                  setMobileMoreOpen(false);
                  navigate("/account/connections");
                }}
                data-testid="mobile-more-relink-google"
                className={`relative w-full flex items-center justify-center gap-2 px-3 h-10 bg-white border text-sm rounded-md ${scopesMissing ? "border-orange-500 text-orange-700" : "border-brand-navy/20 text-brand-navy"}`}
              >
                <RefreshCw size={14} /> Google account permissions
                {scopesMissing && (
                  <span data-testid="mobile-relink-google-badge" className="ml-1 w-2 h-2 rounded-full bg-orange-500" />
                )}
              </button>
              <button onClick={() => { logout(); setMobileMoreOpen(false); }} data-testid="mobile-more-logout" className="w-full flex items-center justify-center gap-2 px-3 h-10 bg-brand-navy text-white text-sm rounded-md">
                <LogOut size={14} /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProdCeoSandboxToggle() {
  const goToSandboxCEO = async () => {
    try {
      await api.post("/sandbox/impersonate", { target_user_id: "sandbox_ceo" });
      toast.success("Switched to CEO Sandbox");
      setTimeout(() => window.location.assign("/dashboard"), 400);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  return (
    <button
      onClick={goToSandboxCEO}
      data-testid="prod-to-sandbox-ceo"
      title="Switch to Sandbox"
      className="inline-flex items-center gap-1.5 px-2 md:px-3 h-9 bg-brand-orange text-white text-xs font-semibold rounded-md hover:bg-brand-orange-600 transition-colors whitespace-nowrap"
    >
      <Sparkles size={13} className="shrink-0" />
      <span className="hidden sm:inline">Switch to Sandbox</span>
      <span className="sm:hidden">Sandbox</span>
    </button>
  );
}

function SandboxProdToggle() {
  const back = async () => {
    try {
      await api.post("/sandbox/restore");
      toast.success("Back en Prod");
      setTimeout(() => window.location.assign("/dashboard"), 400);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  return (
    <button
      onClick={back}
      data-testid="sandbox-to-prod"
      className="inline-flex items-center gap-2 px-3 h-9 bg-brand-navy text-white text-xs font-semibold rounded-md hover:bg-brand-navy-600 transition-colors"
    >
      <Undo2 size={13} /> Basculer en Prod
    </button>
  );
}

function SandboxSwitcher({ isSandboxUser = false }) {
  const [sandboxUsers, setSandboxUsers] = useState([]);
  const [stats, setStats] = useState({ total: 0, empty: true });

  const loadStats = () => {
    api.get("/sandbox/stats").then(r => setStats(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/sandbox/users").then(r => setSandboxUsers(r.data)).catch(() => {});
    loadStats();
  }, []);

  const impersonate = async (uid) => {
    try {
      const r = await api.post("/sandbox/impersonate", { target_user_id: uid });
      toast.success(`Session sandbox ouverte (${r.data.user.role})`);
      setTimeout(() => window.location.assign("/dashboard"), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const purgeOne = async (uid, label) => {
    if (!window.confirm(`Purger les données du profil « ${label} » ? Irréversible.`)) return;
    try {
      const r = await api.post(`/sandbox/purge?target_user_id=${uid}`);
      const total = Object.values(r.data).reduce((a, b) => a + b, 0);
      toast.success(`${label} — ${total} document(s) deleted(s)`);
      loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const purgeAll = async () => {
    if (!window.confirm("Purge ALL sandbox data (across every profile)? Irreversible.")) return;
    try {
      const r = await api.post("/sandbox/purge");
      const total = Object.values(r.data).reduce((a, b) => a + b, 0);
      toast.success(`Sandbox nettoyé — ${total} document(s) deleted(s)`);
      loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const seed = async () => {
    try {
      const r = await api.post("/sandbox/seed");
      toast.success(r.data.message || "Sandbox pre-seeded");
      loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const reset = async () => {
    if (!window.confirm(
      "Reset the sandbox demo?\n\nThis will:\n  • PURGE every sandbox document (across all 6 profiles)\n  • RE-SEED a fresh demo dataset\n\nUseful for onboarding a new admin. Irreversible."
    )) return;
    try {
      const r = await api.post("/sandbox/reset");
      toast.success(`Sandbox reset — ${r.data.purged} doc(s) purged, demo re-seeded`);
      loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <DropdownMenu onOpenChange={(o) => { if (o) loadStats(); }}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="sandbox-switcher-btn"
          title="Tester en tant que…"
          className="flex items-center gap-1.5 px-2 md:px-3 h-9 text-xs font-medium text-brand-navy border border-brand-navy/30 rounded-md hover:bg-brand-cream-200 transition-colors whitespace-nowrap"
        >
          <UserCog size={13} className="shrink-0" />
          <span className="hidden sm:inline">Tester en tant que…</span>
          <span className="sm:hidden">Tester</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex flex-col gap-1">
          <span>Mode sandbox</span>
          <span className="text-[10px] font-normal text-zinc-500 normal-case tracking-normal">
            Les données createds sont isolées et purgeables.
            {stats.total > 0 && <span className="ml-1 font-mono text-brand-navy">({stats.total} doc)</span>}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {sandboxUsers.map((u) => {
          const tagSuffix = u.is_freelance ? "freelance" : (u.user_id.includes("freelance") ? "freelance" : u.role.toLowerCase());
          return (
            <div key={u.user_id} className="flex items-center justify-between gap-1 px-2 py-1.5 hover:bg-zinc-50 rounded-md">
              <button
                type="button"
                data-testid={`sandbox-impersonate-${u.user_id}`}
                onClick={() => impersonate(u.user_id)}
                className="flex-1 flex items-center justify-between gap-3 text-left"
              >
                <span className="text-sm flex items-center gap-1.5">
                  {u.name}
                  {u.is_freelance && <span className="text-[9px] px-1.5 py-0.5 rounded bg-brand-orange-100 text-brand-orange-600 font-mono">FREELANCE</span>}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-brand-navy-500">{u.role}</span>
              </button>
              {isSandboxUser && (
                <button
                  type="button"
                  title={`Purger ${u.name}`}
                  data-testid={`sandbox-purge-${u.user_id}`}
                  onClick={() => purgeOne(u.user_id, u.name)}
                  className="p-1.5 text-zinc-400 hover:text-red-600 hover:bg-red-50 rounded"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          );
        })}
        <DropdownMenuSeparator />
        {isSandboxUser && stats.empty && (
          <DropdownMenuItem
            data-testid="sandbox-seed"
            className="cursor-pointer text-brand-navy hover:!bg-brand-cream-200 focus:!bg-brand-cream-200"
            onClick={seed}
          >
            <Sparkles size={13} className="mr-2" /> Pré-alimenter le sandbox (démo)
          </DropdownMenuItem>
        )}
        {isSandboxUser && (
          <DropdownMenuItem
            data-testid="sandbox-inbox-link"
            className="cursor-pointer text-brand-navy hover:!bg-brand-cream-200 focus:!bg-brand-cream-200"
            onClick={() => { window.location.href = "/sandbox/inbox"; }}
          >
            <Sparkles size={13} className="mr-2" /> Voir Inbox & PDFs simulés
          </DropdownMenuItem>
        )}
        {isSandboxUser && !stats.empty && (
          <DropdownMenuItem
            data-testid="sandbox-reset-demo"
            className="cursor-pointer text-brand-navy hover:!bg-brand-cream-200 focus:!bg-brand-cream-200"
            onClick={reset}
          >
            <Sparkles size={13} className="mr-2" /> Reset sandbox demo (purge + reseed)
          </DropdownMenuItem>
        )}
        {isSandboxUser && (
          <DropdownMenuItem
            data-testid="sandbox-purge-all"
            className="cursor-pointer text-red-700 hover:!bg-red-50 focus:!bg-red-50"
            onClick={purgeAll}
          >
            <AlertTriangle size={13} className="mr-2" /> Purger TOUT le sandbox
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SandboxBanner() {
  const restore = async () => {
    try {
      await api.post("/sandbox/restore");
      toast.success("CEO session restored");
      setTimeout(() => window.location.assign("/dashboard"), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot restore — please log in again");
    }
  };

  return (
    <div
      data-testid="sandbox-banner"
      className="bg-amber-500 text-amber-950 px-6 py-2.5 flex items-center justify-between gap-4 border-b border-amber-600"
    >
      <div className="flex items-center gap-3 text-sm font-medium">
        <AlertTriangle size={16} />
        <span>Sandbox mode active — every created data is isolated and does not impact production.</span>
      </div>
      <button
        onClick={restore}
        data-testid="sandbox-restore-btn"
        className="inline-flex items-center gap-2 px-3 h-8 bg-amber-950/20 hover:bg-amber-950/30 text-amber-950 text-xs font-semibold rounded-md"
      >
        <Undo2 size={13} /> Revenir au compte CEO
      </button>
    </div>
  );
}

// Iter-209 — Yellow banner shown whenever a real CEO is impersonating a
// real production user. Distinct from the SandboxBanner (orange/amber for
// is_test). Calls `/admin/impersonate/stop` to restore the original CEO
// session and reloads to /dashboard.
function ProdImpersonationBanner({ meta }) {
  const stop = async () => {
    try {
      await api.post("/admin/impersonate/stop");
      toast.success("Impersonation arrêtée — session CEO restaurée");
      setTimeout(() => window.location.assign("/dashboard"), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'arrêter l'impersonation");
    }
  };

  return (
    <div
      data-testid="prod-impersonation-banner"
      className="bg-yellow-400 text-yellow-950 px-6 py-2.5 flex items-center justify-between gap-4 border-b border-yellow-600"
    >
      <div className="flex items-center gap-3 text-sm font-medium min-w-0">
        <AlertTriangle size={16} className="shrink-0" />
        <span className="truncate">
          <strong>Impersonation production active</strong> · Tu agis au nom de cet utilisateur, mais ta session reste tracée comme {meta?.actor_name || "CEO"}.
          {meta?.reason ? <span className="ml-2 text-yellow-900/80">— Motif : {meta.reason}</span> : null}
        </span>
      </div>
      <button
        onClick={stop}
        data-testid="prod-impersonation-stop-btn"
        className="inline-flex items-center gap-2 px-3 h-8 bg-yellow-950/20 hover:bg-yellow-950/30 text-yellow-950 text-xs font-semibold rounded-md whitespace-nowrap shrink-0"
      >
        <Undo2 size={13} /> Stop impersonating
      </button>
    </div>
  );
}
